import type {AxiosRequestConfig, CancelTokenSource, Method} from 'axios';
import axios from 'axios';
import configStore from '@deezer/next/services/config';
import {ApiErrorCode} from '@constants/errorCodes';
import requestStorage from './requestStorage';
import {isErrorCode} from '@deezer/next/services/api/utils';
import {Routes} from '@constants/routes';
import Router from 'next/router';
import HttpStatusCode from '@deezer/next/services/types/HttpStatusCode';

export type ApiConfig = {
	requestId?: string;
	preventRedirection?: boolean;
} & AxiosRequestConfig;

const call = async <T = any>(
	method: Method,
	path: string,
	config?: ApiConfig,
): Promise<T> => {
	const requestOptions: AxiosRequestConfig = {
		headers: {
			'X-Requested-With': 'XMLHttpRequest',
		},
		withCredentials: true,
	};

	if (config?.requestId) {
		const source: CancelTokenSource = axios.CancelToken.source();
		requestOptions.cancelToken = source.token;
		requestStorage.add(config.requestId, source);
	}

	const url = `https://${configStore.get('LEGACY_API_HOST')}${path}`;

	try {
		const response = await axios({
			url,
			method,
			...requestOptions,
			...config,
		});

		// Historically redirection to another page was handled by the backend.
		// Check if the legacy server responded with a redirection and manually redirect
		const responseURL = new URL(response.request.responseURL);
		const requestURL = new URL(url);
		if (
			!config?.preventRedirection &&
			responseURL.origin + responseURL.pathname !==
				requestURL.origin + requestURL.pathname
		) {
			const redirectTo = responseURL.pathname + responseURL.search;
			window.location.replace(redirectTo);
		}

		return response.data as T; // TODO this should be removed once Axios provide the right response types
	} catch (e: any) {
		const sessionErrorCode =
			e.response?.data?.details?.code || e.response?.data?.errors?.[0]?.code;
		if (
			e.response?.status === HttpStatusCode.UNAUTHORIZED ||
			isErrorCode(sessionErrorCode, [
				ApiErrorCode.TOKEN_EXPIRED,
				ApiErrorCode.TOKEN_MALFORMED,
				ApiErrorCode.DISABLED_ACCOUNT,
				ApiErrorCode.UNKNOWN_REVOKED_TOKEN,
				ApiErrorCode.TOKEN_INVALID,
			])
		) {
			if (
				Router.pathname !== Routes.LOGOUT &&
				Router.pathname !== Routes.HOME
			) {
				await Router.replace(Routes.LOGOUT);
			}
		}

		throw e;
	} finally {
		requestStorage.remove(config?.requestId);
	}
};

export default {
	get: <T = any>(path: string, config?: ApiConfig) =>
		call<T>('get', path, config),
	post: <T = any>(path: string, data?: any, config?: ApiConfig) =>
		call<T>('post', path, {...config, data}),
	patch: <T = any>(path: string, data?: any, config?: ApiConfig) =>
		call<T>('patch', path, {...config, data}),
	put: <T = any>(path: string, data?: any, config?: ApiConfig) =>
		call<T>('put', path, {...config, data}),
	delete: <T = any>(path: string, config?: ApiConfig) =>
		call<T>('delete', path, config),
	cancel: requestStorage.cancel,
};
